import { useCallback, useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, Plus, Megaphone, Trash, PaperPlaneTilt, Eye, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import MarkdownEditor from "@/components/cms/MarkdownEditor";
import {
  announcementStatus,
  createAnnouncement,
  deleteAnnouncement,
  getAnnouncement,
  listAnnouncements,
  publishAnnouncement,
  unpublishAnnouncement,
  updateAnnouncement,
} from "@/lib/cms/announcements";

const STATUS_TABS = [
  { value: "",          label: "All" },
  { value: "draft",     label: "Drafts" },
  { value: "published", label: "Published" },
];

export default function CmsAnnouncements() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listAnnouncements({ status: status || null });
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load announcements");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.title || "").toLowerCase().includes(q));
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    drafts: rows.filter((r) => !r.published_at).length,
    published: rows.filter((r) => r.published_at).length,
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await deleteAnnouncement(a.id);
      toast.success(`Deleted ${a.title}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const onPublish = async (a) => {
    try {
      await publishAnnouncement(a.id);
      toast.success("Published");
      await load();
    } catch (err) {
      toast.error(err.message || "Publish failed");
    }
  };

  const onUnpublish = async (a) => {
    try {
      await unpublishAnnouncement(a.id);
      toast.success("Unpublished");
      await load();
    } catch (err) {
      toast.error(err.message || "Unpublish failed");
    }
  };

  return (
    <div data-testid="cms-announcements" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Content
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Announcements
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Short posts that families see on their dashboard. Publish to go
              live, unpublish to hide, delete when stale.
            </p>
          </div>
          <button
            type="button"
            data-testid="cms-announcements-new"
            onClick={() => setDrawer({ mode: "create" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <Plus size={14} weight="bold" />
            New announcement
          </button>
        </div>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <Kpi testId="cms-announcements-kpi-total" label="Announcements" value={String(kpis.total)} />
        <Kpi testId="cms-announcements-kpi-drafts" label="Drafts" value={String(kpis.drafts)} />
        <Kpi testId="cms-announcements-kpi-published" label="Published" value={String(kpis.published)} accent />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <input
            data-testid="cms-announcements-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title"
            className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              data-testid={`cms-announcements-filter-${t.value || "all"}`}
              onClick={() => setStatus(t.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                status === t.value
                  ? "border-primary/40 bg-primary text-white"
                  : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyCard testId="cms-announcements-empty" title="No announcements yet" desc="Create your first announcement to share with families." />
      ) : (
        <ul data-testid="cms-announcements-list" className="space-y-2">
          {filtered.map((a) => {
            const s = announcementStatus(a);
            return (
              <li
                key={a.id}
                data-testid={`cms-announcements-row-${a.id}`}
                className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30">
                    <Megaphone size={18} weight="duotone" className="text-primary-hover" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-heading text-base font-semibold text-neutral-900">
                      {a.title}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      {s === "published"
                        ? `Published ${formatDate(a.published_at)}`
                        : `Draft · updated ${formatDate(a.updated_at || a.created_at)}`}
                    </p>
                    <span
                      data-testid={`cms-announcements-row-${a.id}-status`}
                      className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        s === "published"
                          ? "border-primary/30 bg-primary/10 text-primary-hover"
                          : "border-neutral-200 bg-neutral-100 text-neutral-600"
                      }`}
                    >
                      {s}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid={`cms-announcements-row-${a.id}-edit`}
                    onClick={() => setDrawer({ mode: "edit", announcementId: a.id })}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                  >
                    <Pencil size={11} weight="bold" />
                    Edit
                  </button>
                  {s === "draft" ? (
                    <button
                      type="button"
                      data-testid={`cms-announcements-row-${a.id}-publish`}
                      onClick={() => onPublish(a)}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover"
                    >
                      <PaperPlaneTilt size={11} weight="bold" />
                      Publish
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`cms-announcements-row-${a.id}-unpublish`}
                      onClick={() => onUnpublish(a)}
                      className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
                    >
                      <Eye size={11} weight="bold" />
                      Unpublish
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`cms-announcements-row-${a.id}-delete`}
                    onClick={() => onDelete(a)}
                    aria-label={`Delete announcement: ${a.title}`}
                    title="Delete announcement"
                    className="inline-flex items-center rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                  >
                    <Trash size={11} weight="bold" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {drawer && (
        <AnnouncementSheet
          mode={drawer.mode}
          announcementId={drawer.announcementId}
          organizationId={profile?.organization_id}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function AnnouncementSheet({ mode, announcementId, organizationId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", body_md: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");

  useEffect(() => {
    if (mode !== "edit" || !announcementId) return;
    (async () => {
      try {
        const a = await getAnnouncement(announcementId);
        if (a) {
          setForm({ title: a.title || "", body_md: a.body_md || "" });
          setStatus(announcementStatus(a));
        }
      } catch (err) {
        toast.error(err.message || "Could not load announcement");
      }
    })();
  }, [mode, announcementId]);

  const save = async (publish) => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (mode === "create") {
        await createAnnouncement({
          organizationId,
          title: form.title.trim(),
          bodyMd: form.body_md,
          publish: Boolean(publish),
        });
      } else {
        const patch = {
          title: form.title.trim(),
          body_md: form.body_md,
        };
        if (publish === true) patch.published_at = new Date().toISOString();
        if (publish === false) patch.published_at = null;
        await updateAnnouncement(announcementId, patch);
      }
      toast.success(publish ? "Published" : "Saved");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="cms-announcement-sheet">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "New announcement" : "Edit announcement"}</SheetTitle>
          <SheetDescription>Short posts for family dashboards. Save as draft or publish directly.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Title</span>
            <input
              data-testid="cms-announcement-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Holiday closure on Friday"
              className={input}
            />
          </label>
          <div>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-neutral-500">Body</p>
            <MarkdownEditor
              value={form.body_md}
              onChange={(v) => setForm({ ...form, body_md: v })}
              organizationId={organizationId}
              testIdPrefix="cms-announcement-body"
              minRows={8}
              placeholder="Write a quick note to families…"
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              data-testid="cms-announcement-save-draft"
              disabled={saving}
              onClick={() => save(mode === "create" ? false : (status === "published" ? undefined : false))}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-600 shadow-sm hover:bg-neutral-50 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              type="button"
              data-testid="cms-announcement-publish"
              disabled={saving}
              onClick={() => save(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              <PaperPlaneTilt size={12} weight="duotone" />
              {saving ? "Publishing…" : status === "published" ? "Save & republish" : "Publish"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" /></div>;
}

function EmptyCard({ title, desc, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <p className="font-heading text-base font-semibold text-neutral-900">{title}</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">{desc}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}
